import { ArrowUpRight } from "lucide-react";
import { iconByName } from "@/lib/icons";
import { Link } from "react-router-dom";
import { CAPABILITIES } from "@/data/content";
import { Reveal, SectionHeader } from "@/components/Reveal";

const Capabilities = () => (
    <section id="capabilities" data-testid="capabilities-section" className="scroll-mt-24 relative overflow-hidden bg-grid-dark t-bg py-14 md:py-24">
        <img loading="lazy" decoding="async" src="/backgrounds/04-services.webp" alt="" aria-hidden="true" className="plate pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14]" />
        <div className="animate-aurora motion-reduce:[animation:none] pointer-events-none absolute -left-48 top-[-10%] h-[30rem] w-[30rem] rounded-full bg-electric/8 blur-[130px]" aria-hidden="true" />
        <div className="animate-aurora-slow motion-reduce:[animation:none] pointer-events-none absolute -right-48 bottom-[-20%] h-[28rem] w-[28rem] rounded-full bg-azure/6 blur-[130px]" aria-hidden="true" />
        <div className="container-x relative">
            <Reveal>
                <div className="flex flex-wrap items-end justify-between gap-8">
                    <SectionHeader
                        eyebrow="Capabilities"
                        title="Strategy, engineering, security and operations — under one roof"
                        intro="Six integrated services that keep your technology secure, reliable and scalable — from strategy through 24/7 operations."
                    />
                    <Link to="/contact" data-testid="capabilities-cta-link" className="btn-primary !px-6 !py-2.5">
                        Discuss your challenge <ArrowUpRight size={15} />
                    </Link>
                </div>
            </Reveal>

            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {CAPABILITIES.map((cap, i) => {
                    const Icon = iconByName(cap.icon);
                    return (
                        <Reveal key={cap.code} delay={(i % 3) * 0.08}>
                            <article
                                data-testid={`capability-card-${cap.code.toLowerCase()}`}
                                className="card-dark group relative flex h-full flex-col overflow-hidden rounded-2xl p-8"
                            >
                                <div className="relative -mx-8 -mt-8 mb-7 h-44 overflow-hidden">
                                    <img
                                        src={cap.visual}
                                        alt=""
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy-800 via-navy-800/25 to-transparent" />
                                </div>
                                <div className="flex items-start justify-between">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-electric/25 bg-electric/10 text-electric transition-colors duration-300 group-hover:bg-electric t-gh-heading">
                                        <Icon size={21} />
                                    </span>
                                    <span className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] t-faint">
                                        {cap.code}
                                    </span>
                                </div>
                                <h3 className="mt-7 font-display text-xl font-semibold t-heading md:text-2xl">{cap.title}</h3>
                                <p className="mt-3 flex items-start gap-2.5 text-[15px] font-semibold leading-snug t-heading">
                                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-electric" aria-hidden="true" />
                                    {cap.outcome}
                                </p>
                                <p className="mt-2.5 flex-1 text-sm leading-relaxed t-soft">{cap.desc}</p>
                                <ul className="mt-6 flex flex-wrap gap-2">
                                    {cap.points.map((p) => (
                                        <li
                                            key={p}
                                            className="rounded-full border t-border px-3 py-1 font-mono text-[10.5px] uppercase tracking-wider t-faint"
                                        >
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={`/services/${cap.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                                    data-testid={`capability-learn-more-${cap.code.toLowerCase()}`}
                                    className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-electric opacity-80 transition-[opacity,gap] duration-300 hover:gap-2.5 hover:opacity-100"
                                >
                                    Learn more <ArrowUpRight size={15} />
                                </Link>
                            </article>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

export default Capabilities;
